import OscillographyService from '@/domain/services/generalPanel/reports/oscillography';
import ModuleMenuService from '@/domain/services/installation/modules/menu';
import Http from '@/services/http';
import { toTimeZone } from '@/services/dateTime/datetimeService';
import { timezones } from '@/constants/localizer';
import { ModuleSubtype, ModuleTypes } from 'sigma-mappings';
import {
  diagnosticManeuverType,
} from '@/domain/enums/installation/engineering/oscillographyAnalysis/oscillographyAnalysis';

const OSCILLOGRAPHY_ANALYSIS_MODULE_ID = '0338ECAD-1533-42CC-9971-B76C41DE679F';
const MANEUVER_SUBTYPES = ['ordinary', 'learning', 'orphan'];
const ALARM_MANEUVER_TYPES = {
  breaker: [diagnosticManeuverType.OPENING, diagnosticManeuverType.CLOSING],
  motor: [diagnosticManeuverType.MOTOR],
};

const VARIABLE_KEYS = {
  tempoMovimentacaoContato: 'contact-movement-time',
  tempoInterrupcaoCorrente: 'current-interruption-time',
  tempoArco: 'arc-time',
  tempoCorteCorrenteBobina: 'coil-current-cutoff-time',
  resistenciaBobina: 'coil-resistance',
  tensaoMinimaBobina: 'minimum-coil-voltage',
  tensaoMaximaBobina: 'maximum-coil-voltage',
  strokePolo: 'pole-stroke',
  overtravelPolo: 'pole-overtravel',
  reboundPolo: 'pole-rebound',
  tempoOvertravelPolo: 'pole-overtravel-time',
  tempoReboundPolo: 'pole-rebound-time',
  tempoAmortecimentoPolo: 'pole-damping-time',
  tempoInicioMovimentoPolo: 'pole-movement-start-time',
  tempoContatoAuxiliarDja: 'auxiliary-contact-dja-time',
  tempoContatoAuxiliarDjb: 'auxiliary-contact-djb-time',
  velocidadeMedia: 'average-speed',
  tempoEstabelecimentoCorrente: 'current-establishment-time',
  tempoPreArco: 'pre-arcing-time',
  tempoMagneticoBobina: 'coil-magnetic-time',
  tempoPulsoBobina: 'coil-pulse-time',
  tensaoMaximaBobinaAntesTempoMagnetico: 'maximum-coil-voltage-before-magnetic-time',
  correnteMaximaBobinaAntesTempoMagnetico: 'maximum-coil-current-before-magnetic-time',
  variacaoCorrenteBobinaAntesTempoMagnetico: 'coil-current-variation-before-magnetic-time',
  strokeEixoFechamento: 'closing-axis-stroke',
  overtravelEixoFechamento: 'closing-axis-overtravel',
  tempoOvertravelEixoFechamento: 'closing-axis-overtravel-time',
  tempoAmortecimentoEixoFechamento: 'closing-axis-damping-time',
  tensaoMinimaMotor: 'minimum-motor-voltage',
  tensaoMaximaMotor: 'maximum-motor-voltage',
  energiaUsadaOperacao: 'operation-energy',
  tempoOperacaoMotorCarregamentoMola: 'motor-spring-charging-time',
  correnteMaximaMotor: 'maximum-motor-current',
  picoCorrenteMotor: 'motor-current-peak',
};

const MANEUVER_SIDES = {
  [diagnosticManeuverType.OPENING]: 'opening',
  [diagnosticManeuverType.CLOSING]: 'closing',
  [diagnosticManeuverType.MOTOR]: 'motor',
  Abertura: 'opening',
  Fechamento: 'closing',
  Motor: 'motor',
};

const emptyReport = () => ({
  chartData: [],
  diagnosticsChartData: [],
  maneuvers: [],
});

function getOscillographsAmount(value) {
  return Number((value || '').split('-')[1]) || 10;
}

function getLatestManeuvers(maneuvers, amount, assetModuleId) {
  const normalizedAssetModuleId = assetModuleId?.toUpperCase();
  const filteredManeuvers = assetModuleId
    ? maneuvers.filter(
      maneuver => maneuver.assetModuleId?.toUpperCase() === normalizedAssetModuleId,
    )
    : maneuvers;

  const sortedManeuvers = [...filteredManeuvers]
    .sort((first, second) => (
      new Date(second.dateAndTime).getTime() - new Date(first.dateAndTime).getTime()
    ));

  if (assetModuleId) {
    return sortedManeuvers.slice(0, amount);
  }

  const assetModuleIds = [...new Set(sortedManeuvers
    .map(maneuver => maneuver.assetModuleId?.toUpperCase())
    .filter(Boolean))];

  if (assetModuleIds.length <= 1) {
    return sortedManeuvers.slice(0, amount);
  }

  const maneuversByAssetModule = new Map();
  sortedManeuvers.forEach((maneuver) => {
    const maneuverAssetModuleId = maneuver.assetModuleId?.toUpperCase();
    if (!maneuverAssetModuleId) return;

    const assetModuleManeuvers = maneuversByAssetModule.get(maneuverAssetModuleId) || [];
    if (assetModuleManeuvers.length >= amount) return;

    assetModuleManeuvers.push(maneuver);
    maneuversByAssetModule.set(maneuverAssetModuleId, assetModuleManeuvers);
  });

  return [...maneuversByAssetModule.values()]
    .flat()
    .sort((first, second) => (
      new Date(second.dateAndTime).getTime() - new Date(first.dateAndTime).getTime()
    ));
}

function convertManeuversToUtc(maneuvers) {
  return maneuvers.map(maneuver => ({
    ...maneuver,
    dateAndTime: maneuver.dateAndTime
      ? toTimeZone(maneuver.dateAndTime, timezones.UTC)
      : maneuver.dateAndTime,
  }));
}

function filterManeuversWithoutDiagnostics(reportData) {
  return {
    ...reportData,
    chartData: [],
    maneuvers: (reportData.maneuvers || []).filter(maneuver => (
      maneuver.infoData?.diagnostics?.some(diagnostic => diagnostic.hasValue)
    )),
  };
}

function mapAlarmRows(results, phase, totalOscillographs) {
  const rows = new Map();
  const totals = {
    opening: 0,
    closing: 0,
    motor: 0,
  };

  results.forEach(({ data, maneuverType }) => {
    const side = MANEUVER_SIDES[maneuverType];
    if (!side) return;

    totals[side] += totalOscillographs;

    Object.entries(data?.frequencias || {}).forEach(([variable, count]) => {
      const variableKey = VARIABLE_KEYS[variable];

      if (!variableKey) return;

      const rowKey = `variables.${variableKey}`;
      const row = rows.get(rowKey) || {
        name: rowKey,
        phase,
        opening: { count: 0, total: 0, percentage: 0 },
        closing: { count: 0, total: 0, percentage: 0 },
        motor: { count: 0, total: 0, percentage: 0 },
      };

      rows.set(rowKey, {
        ...row,
        [side]: {
          ...row[side],
          count: row[side].count + count,
        },
      });
    });
  });

  rows.forEach((row, rowKey) => {
    const normalizedRow = Object.keys(totals).reduce((result, side) => ({
      ...result,
      [side]: {
        ...result[side],
        total: totals[side],
        percentage: totals[side]
          ? (result[side].count / totals[side]) * 100
          : 0,
      },
    }), row);
    rows.set(rowKey, normalizedRow);
  });

  return [...rows.values()];
}

async function createTransientReport({
  assetId,
  assetTypeId,
  installationId,
  maneuverType,
  moduleIds,
  oscillographsAmount,
  assetModuleId,
}) {
  const { maneuvers = [] } = await OscillographyService.getManeuvers({
    assets: [assetId],
    modules: moduleIds,
    maneuverTypes: [maneuverType],
    maneuverSubTypes: MANEUVER_SUBTYPES,
    registeredOscillographs: [],
  });
  const selectedManeuvers = getLatestManeuvers(
    maneuvers,
    oscillographsAmount,
    assetModuleId,
  );

  if (!selectedManeuvers.length) {
    return null;
  }

  const { variables = [] } = await OscillographyService.getVariables({
    assetTypeId,
    maneuverType: [maneuverType],
    moduloId: moduleIds[0],
  });

  if (!variables.length) {
    return null;
  }

  const { reportId } = await OscillographyService.saveReport({
    assetTypeId,
    transient: true,
    template: {
      maneuvers: convertManeuversToUtc(selectedManeuvers),
      variables: variables.map(variable => variable.id),
      referenceVariables: [],
      installations: [{
        installationId,
        installationName: '',
      }],
      assets: [assetId],
      maneuverTypes: [maneuverType],
      maneuverSubTypes: MANEUVER_SUBTYPES,
    },
  });

  return reportId;
}

export default {
  emptyReport,

  async getStatus(assetModuleId) {
    if (!assetModuleId) {
      return { lastUpdate: '' };
    }

    const http = new Http();
    const { data } = await http.get(
      `/engenharia/analise-oscilografia/${assetModuleId}/status`,
    );

    return {
      lastUpdate: data.ultimaAtualizacao || '',
    };
  },

  async getModules(assetId) {
    const [reportModules, menuItems] = await Promise.all([
      OscillographyService.getModules([assetId]),
      ModuleMenuService.getMenuItems(assetId),
    ]);

    return {
      reportModuleIds: reportModules
        .filter(module => module.id.toUpperCase() !== OSCILLOGRAPHY_ANALYSIS_MODULE_ID)
        .map(module => module.id),
      phases: menuItems.filter(
        module => module.moduleType === ModuleTypes.SUPERVISION
          && module.subtype === ModuleSubtype.IDX,
      ),
    };
  },

  async getDiagnostics({
    assetId,
    assetTypeId,
    installationId,
    maneuverType,
    moduleIds = [],
    assetModuleId,
    oscillographs,
    chartFilters,
  }) {
    if (!assetId || !assetTypeId || !installationId || !moduleIds.length) {
      return {
        reportId: null,
        reportData: emptyReport(),
      };
    }

    const reportId = await createTransientReport({
      assetId,
      assetTypeId,
      installationId,
      maneuverType,
      moduleIds,
      oscillographsAmount: getOscillographsAmount(oscillographs),
      assetModuleId,
    });

    if (!reportId) {
      return {
        reportId: null,
        reportData: emptyReport(),
      };
    }

    const reportData = filterManeuversWithoutDiagnostics(
      await OscillographyService.getReportView(reportId, chartFilters, {
        includeChartData: false,
        includeOscillographyCurves: false,
      }),
    );
    return {
      reportId,
      reportData,
    };
  },

  async refreshDiagnostics(reportId, chartFilters) {
    if (!reportId) {
      return emptyReport();
    }

    const reportData = filterManeuversWithoutDiagnostics(
      await OscillographyService.getReportView(reportId, chartFilters, {
        includeChartData: false,
        includeOscillographyCurves: false,
      }),
    );

    return reportData;
  },

  async getAlarms({
    assetModuleId,
    assetModuleIds = [],
    alarmMode,
    oscillographs,
    phase = '',
  }) {
    if (!assetModuleId) return [];

    const http = new Http();
    const amount = getOscillographsAmount(oscillographs);
    const selectedAssetModuleIds = assetModuleIds.length ? assetModuleIds : [assetModuleId];
    const maneuverTypes = ALARM_MANEUVER_TYPES[alarmMode]
      || ALARM_MANEUVER_TYPES.breaker;
    const results = (await Promise.all(
      selectedAssetModuleIds.flatMap(selectedAssetModuleId => maneuverTypes.map(
        async maneuverType => {
          try {
            return {
              maneuverType,
              data: (await http.post(
                `/engenharia/analise-oscilografia/${selectedAssetModuleId}/alarmes`,
                {
                  tiposManobra: [maneuverType],
                  quantidadeUltimasOscilografias: amount,
                },
              )).data,
            };
          } catch {
            return null;
          }
        },
      )),
    )).filter(Boolean);

    return mapAlarmRows(results, phase, amount);
  },
};

export { mapAlarmRows, OSCILLOGRAPHY_ANALYSIS_MODULE_ID };
